import { format } from 'date-fns';
import { SubscriptionStatus, PlanType } from '../../types/api';
import { useTranslation } from 'react-i18next';

interface SubscriptionState {
  id?: string;
  plan?: PlanType | null;
  status?: SubscriptionStatus | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface NoSubscriptionViewProps {
  cancelledSubscriptions: SubscriptionState[];
  onToggleCancelled: () => void;
  onSelectCancelled: (subscription: SubscriptionState) => void;
  onStartSubscription: () => void;
  hasExpiredSubscriptions: boolean;
  displayExpired: boolean;
}

const NoSubscriptionView: React.FC<NoSubscriptionViewProps> = ({
  cancelledSubscriptions,
  onToggleCancelled,
  onSelectCancelled,
  onStartSubscription,
  hasExpiredSubscriptions,
  displayExpired
}) => {
  const { t } = useTranslation();
  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4">{t('subscription.subscriptionsList.noActiveSubscriptionTitle')}</h2>
          <p className="text-gray-600 mb-6">
            {t('subscription.subscriptionsList.noActiveSubscriptionDescription')}
          </p>
          
          <button
            onClick={onStartSubscription}
            className="bg-pink-600 hover:bg-pink-700 text-white font-medium py-2 px-6 rounded-md transition duration-200"
          >
            {t('subscription.subscriptionsList.startSubscription')}
          </button>
        </div>
      </div>

      {hasExpiredSubscriptions && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">{t('subscription.subscriptionsList.inactiveSubscriptions')}</h3>
            <button 
              onClick={onToggleCancelled}
              className="text-sm text-pink-600 hover:text-pink-800"
            >
              {displayExpired ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {displayExpired && (
            <div className="space-y-4">
              {cancelledSubscriptions.map((sub, index) => (
                <div 
                  key={index}
                  className="border rounded-md p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => onSelectCancelled(sub)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">
                        {sub.plan ? (typeof sub.plan === 'string' ? sub.plan : 'Standard') : 'Standard Subscription'}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {sub.startDate && sub.endDate ? (
                          <>
                            From {format(new Date(sub.startDate), 'MMM d, yyyy')} to {format(new Date(sub.endDate), 'MMM d, yyyy')}
                          </>
                        ) : (
                          'Dates not available'
                        )}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                      Cancelled
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NoSubscriptionView; 